<template>
  <v-container class="planning-project-list">
    <!-- Loader overlay -->
    <v-overlay :value="!isLoaded">
      <v-progress-circular
        indeterminate
        size="64"
      />
    </v-overlay>

    <template v-if="isLoaded">
      <v-row>
        <v-col>
          <v-select
            v-model="selectedDateType"
            :items="dateTypes"
            label="Type de date"
          />
        </v-col>

        <v-col>
          <v-dialog
            v-model="dateDialog"
            width="290px"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                :value="selectedDate"
                label="Date"
                prepend-icon="mdi-calendar"
                readonly
                :disabled="!selectedDateType"
                clearable
                v-on="on"
              />
            </template>
            <v-date-picker v-model="selectedDate">
              <v-spacer />
              <v-btn
                text
                color="primary"
                @click="dateDialog = false"
              >
                OK
              </v-btn>
            </v-date-picker>
          </v-dialog>
        </v-col>

        <v-col>
          <v-btn @click="search">
            Rechercher
          </v-btn>
        </v-col>
      </v-row>

      <div v-if="projects.length">
        <p>
          Attention ! Cliquer sur le bouton synchroniser effacera de la tablette
          tous les projets ne correspondant à cette date. Les projets qui ont
          déjà été transférés sont sauvegardés les autres seront perdus
        </p>
      </div>

      <v-data-table
        :headers="headers"
        :items="localProjects"
        :items-per-page="15"
      >
        <template #item="{item}">
          <tr>
            <td class="text-start">
              {{ item.applicator.firstname }} {{ item.applicator.lastname }}
            </td>
            <td class="text-start">
              {{ item.product_name }}
            </td>
            <td class="text-start">
              {{ item.created_at }}
            </td>
          </tr>
        </template>
      </v-data-table>

      <div class="save-container">
        <v-btn
          class="save"
          :disabled="!projects || !projects.length"
          @click="saveSelectedProjects"
        >
          Récuperation des fiches
        </v-btn>
        <v-btn
          class="save"
          @click="$router.push('/')"
        >
          Base Tablette
        </v-btn>
      </div>
    </template>
  </v-container>
</template>

<script>
import { productsModelObject } from "@/mixins/productsModelObject.js";

import { synchronization } from "@/mixins/synchronization.js";

export default {
  name: "PlanningPage",

  mixins: [synchronization, productsModelObject],

  data() {
    return {
      isLoaded: true,
      selectedDateType: null,
      projects: [],

      headers: [
        {
          text: "Applicateur",
          value: "patient.fullname",
          sortable: false,
        },
        {
          text: "Fiche",
          value: "product_name",
        },
        {
          text: "Date",
          value: "created_at",
        },
      ],

      dateTypes: [
        {
          text: "Arrivée de moulage",
          value: "ARRIVAL",
        },
        {
          text: "Retour d'essayage",
          value: "RETURN",
        },
      ],

      dateDialog: false,
      dateDialogSelected: null,
      selectedDate: null,
    };
  },
  computed: {
    localProjects() {
      return this.projects.map((project) => {
        project.product_name = this.productsModelObject()[
          project.product_model
        ].name;
        return project;
      });
    },
  },

  methods: {
    // Get applicator name
    async getApplicator(id) {
      const applicator = await this.$db.users.get(id);

      return applicator.firstname + " " + applicator.lastname;
    },

    async search() {
      // Loading state
      this.isLoaded = false;

      // Check selected date type
      const dateType = this.selectedDateType;

      if (dateType === "ARRIVAL") {
        const { data } = await this.$store.dispatch("projects/fetchProjects", {
          molding_arrival: this.selectedDate,
        });

        this.projects = data;
      } else if (dateType === "RETURN") {
        const { data } = await this.$store.dispatch("projects/fetchProjects", {
          fitting_return: this.selectedDate,
        });

        this.projects = data;
      }

      this.isLoaded = true;
    },

    // Save selected projects in storage
    async saveSelectedProjects() {
      try {
        this.isLoaded = false;

        this.emptyIndexedDB();

        for (const project of this.projects) {
          await Promise.all([
            this.saveProject(project),
            this.saveMedias(project),
            this.saveProduct(project),
          ]).catch((e) => {
            const errorMsg = `La fiche ${project.product_model} de ${project.patient.fullname}, n'a pas pu être récupérée !`;
            this.handleError(e, errorMsg); // TODO: Send error to server
            this.rollback(project);
            this.counter-- + 1;
            return; // Go to next project
          });

          this.counter++ + 1;
          this.completePercetage = (this.counter / this.projects.length) * 100;
        }

        this.$notify({
          group: "main",
          type: "success",
          title: "Projets enregistrés !",
          text: `${this.projects.length} projets ont été enregistrés`,
          duration: 5000,
        });

        this.counter = 0;
        this.completePercetage = 0;
      } finally {
        this.isLoaded = true;
      }
    },

    // Save project + patient
    async saveProject(project) {
      try {
        // Try to get patient from local index
        const patientKey = await this.$db.patients.get(project.patient.uuid);

        // Add patient if not present in local db
        if (!patientKey) {
          await this.$db.patients.add(project.patient);
        }

        // Check if project already exist in local db
        const projectKey = await this.$db.projects.get(project.uuid);

        // Add project if not present in local db
        if (!projectKey) {
          // Fetch project from Api
          const projectFromApi = await this.$store.dispatch(
            "projects/fetchProject",
            project.uuid
          );

          // Set prop to keep track of product versioning
          projectFromApi.local_version = null;
          projectFromApi.server_version = project.product_uuid;

          // Save project in local db
          await this.$db.projects.add(projectFromApi);
        }
      } catch (error) {
        throw error;
      }
    },

    // Save project medias
    async saveMedias(project) {
      try {
        for (const media of project.medias) {
          const mediaFromApi = await this.$store.dispatch("medias/fetchMedia", {
            project_uuid: project.uuid,
            media_uuid: media.uuid,
          });

          // Deep clone
          let mediaToStore = JSON.parse(JSON.stringify(mediaFromApi));
          mediaToStore.project_uuid = project.uuid;

          // Check if we need to fetch an image
          if (mediaToStore.has_image) {
            // Fetch image media
            const imageMediaFromApi = await this.$store.dispatch(
              "medias/fetchImageMedia",
              {
                project_uuid: project.uuid,
                media_uuid: mediaToStore.uuid,
              }
            );

            mediaToStore.image = imageMediaFromApi.image;
          }

          const mediaKey = await this.$db.medias.get(mediaToStore.uuid);

          if (!mediaKey) {
            await this.$db.medias.add(mediaToStore);
          }
        }
      } catch (error) {
        throw error;
      }
    },

    // Save project product
    async saveProduct(project) {
      try {
        // Check if there is a product uuid
        if (!project.product_uuid) {
          throw new Error("Ce project n'a pas de fiche");
        }

        const productsModelObject = this.productsModelObject();
        const tableName = productsModelObject[project.product_model].table;
        // Fetch product from Api
        const productFromApi = await productsModelObject[
          project.product_model
        ].get(project.product_uuid);

        // Deep clone
        const productToStore = JSON.parse(JSON.stringify(productFromApi));

        // Fetch product from local db
        const productKey = await tableName.get(productToStore.uuid);

        // Check if product already exist
        if (!productKey) {
          await tableName.add(productToStore);
        }
      } catch (error) {
        throw error;
      }
    },

    async emptyIndexedDB() {
      await this.$db.patients.clear();
      await this.$db.projects.clear();
      await this.$db.medias.clear();
    },
  },
};
</script>

<style lang="scss">
.planning-project-list {
  display: flex;
  flex-direction: column;
  padding-bottom: 66px;

  .v-data-table {
    flex-grow: 1;

    table {
      border: none !important;

      * {
        border: none !important;
      }

      tbody {
        td {
          font-size: 14px !important;

          padding: 20px 16px !important;
          &:nth-last-child(5) {
            font-weight: bold;
            font-size: 13px !important;
            color: #006064;
            text-transform: uppercase;
          }

          &:nth-last-child(4) {
            font-weight: bold;
            font-size: 13px !important;
            text-transform: uppercase;
            color: #880e4f;
          }

          &:nth-last-child(3) {
            font-weight: 600;
            color: #880e4f;
          }

          &:nth-last-child(2) {
            i {
              font-size: 20px !important;
            }
          }
        }
      }
    }
    .v-data-footer {
      border-top: none !important;
    }
  }
}
</style>
